/* ============================================================
   NSC ARTICLE  —  assets/nsc-article.js
   Restructures legacy post bodies at runtime so 70+ existing
   articles gain navigation, anchors, responsive tables, FAQ
   accordions and an inline product CTA without being re-edited.
   No dependencies. Safe to load with defer.
   ============================================================ */
(function () {
  'use strict';

  var root = document.querySelector('[data-nsc-article]');
  if (!root) return;

  var prose = root.querySelector('[data-nsc-prose]');
  if (!prose) return;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- helpers ---------- */
  function slugify(str) {
    return str.toLowerCase().trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .slice(0, 60) || 'section';
  }

  function uniqueId(base, used) {
    var id = base, i = 2;
    while (used[id] || document.getElementById(id)) { id = base + '-' + i; i++; }
    used[id] = true;
    return id;
  }

  /* ---------- 1. Headings: ids + copyable anchors ---------- */
  var headings = Array.prototype.slice.call(prose.querySelectorAll('h2, h3'))
    .filter(function (h) { return h.textContent.trim().length > 1; });

  var used = {};
  headings.forEach(function (h) {
    if (!h.id) h.id = uniqueId(slugify(h.textContent), used);
    h.classList.add('nsc-h-anchored');
    var a = document.createElement('a');
    a.className = 'nsc-anchor';
    a.href = '#' + h.id;
    a.setAttribute('aria-label', 'Link to this section');
    a.textContent = '#';
    h.appendChild(a);
  });

  /* ---------- 2. Table of contents (desktop rail + mobile) ---------- */
  var minHeadings = parseInt(root.dataset.tocMin || '3', 10);
  var h2s = headings.filter(function (h) { return h.tagName === 'H2'; });

  if (h2s.length >= minHeadings) {
    var lists = root.querySelectorAll('[data-nsc-toc-list], [data-nsc-toc-list-mobile]');

    lists.forEach(function (list) {
      headings.forEach(function (h) {
        var li = document.createElement('li');
        li.className = h.tagName === 'H3' ? 'nsc-toc__item nsc-toc__item--sub' : 'nsc-toc__item';
        var link = document.createElement('a');
        link.href = '#' + h.id;
        link.textContent = h.textContent.replace(/#$/, '').trim();
        link.dataset.target = h.id;
        li.appendChild(link);
        list.appendChild(li);
      });
    });

    var toc = root.querySelector('[data-nsc-toc]');
    var tocMobile = root.querySelector('[data-nsc-toc-mobile]');
    if (toc) toc.hidden = false;
    if (tocMobile) tocMobile.hidden = false;

    /* scrollspy */
    if ('IntersectionObserver' in window) {
      var links = root.querySelectorAll('[data-nsc-toc-list] a');
      var byId = {};
      links.forEach(function (l) { byId[l.dataset.target] = l; });

      var spy = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          var link = byId[e.target.id];
          if (!link) return;
          if (e.isIntersecting) {
            links.forEach(function (l) { l.classList.remove('is-current'); });
            link.classList.add('is-current');
          }
        });
      }, { rootMargin: '-15% 0px -70% 0px', threshold: 0 });

      headings.forEach(function (h) { spy.observe(h); });
    }

    /* smooth scroll with sticky-header offset */
    root.addEventListener('click', function (e) {
      var a = e.target.closest('a[href^="#"]');
      if (!a) return;
      var target = document.getElementById(a.getAttribute('href').slice(1));
      if (!target) return;
      e.preventDefault();
      var offset = 90;
      var top = target.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top: top, behavior: reduceMotion ? 'auto' : 'smooth' });
      history.replaceState(null, '', a.getAttribute('href'));
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
      if (tocMobile) tocMobile.open = false;
    });
  }

  /* ---------- 3. Tables: responsive wrapper + stacked mobile view ---------- */
  Array.prototype.forEach.call(prose.querySelectorAll('table'), function (table) {
    if (table.parentElement.classList.contains('nsc-table-wrap')) return;

    var wrap = document.createElement('div');
    wrap.className = 'nsc-table-wrap';
    wrap.setAttribute('role', 'region');
    wrap.setAttribute('tabindex', '0');
    wrap.setAttribute('aria-label', 'Comparison table, scrollable');
    table.parentNode.insertBefore(wrap, table);
    wrap.appendChild(table);
    table.classList.add('nsc-table');

    /* promote first row to a real thead if the author never did */
    if (!table.tHead && table.rows.length > 1) {
      var first = table.rows[0];
      var thead = table.createTHead();
      thead.appendChild(first);
      Array.prototype.forEach.call(first.cells, function (c) {
        if (c.tagName === 'TD') {
          var th = document.createElement('th');
          th.innerHTML = c.innerHTML;
          th.scope = 'col';
          c.replaceWith(th);
        }
      });
    }

    /* data-label on body cells so mobile can stack them */
    var heads = Array.prototype.map.call(
      table.querySelectorAll('thead th'),
      function (th) { return th.textContent.trim(); }
    );
    if (heads.length) {
      Array.prototype.forEach.call(table.querySelectorAll('tbody tr'), function (tr) {
        Array.prototype.forEach.call(tr.cells, function (td, i) {
          if (heads[i]) td.setAttribute('data-label', heads[i]);
        });
      });
    }
  });

  /* ---------- 4. Outbound links: rel hygiene ---------- */
  var host = root.dataset.shopHost || window.location.host;
  Array.prototype.forEach.call(prose.querySelectorAll('a[href^="http"]'), function (a) {
    var isInternal = a.host === window.location.host || a.host === host;
    if (isInternal) return;
    a.target = '_blank';
    var rel = (a.rel || '').split(/\s+/).filter(Boolean);
    ['sponsored', 'nofollow', 'noopener'].forEach(function (r) {
      if (rel.indexOf(r) === -1) rel.push(r);
    });
    a.rel = rel.join(' ');
    a.classList.add('nsc-outbound');
  });

  /* ---------- 5. FAQ: convert flat text into accordions ---------- */
  if (root.dataset.faqAccordion === 'true') {
    var faqHeading = headings.filter(function (h) {
      return /frequently asked|faqs?\b|questions answered/i.test(h.textContent);
    })[0];

    if (faqHeading) {
      var node = faqHeading.nextElementSibling;
      var pending = null;
      var container = document.createElement('div');
      container.className = 'nsc-faq nsc-faq--auto';
      var made = 0;

      while (node && !/^H[12]$/.test(node.tagName)) {
        var next = node.nextElementSibling;
        var text = node.textContent.trim();
        var isQuestion =
          (node.tagName === 'H3') ||
          (node.tagName === 'P' && /\?$/.test(text) && text.length < 160);

        if (isQuestion) {
          pending = document.createElement('details');
          pending.className = 'nsc-faq__item';
          var summary = document.createElement('summary');
          summary.textContent = text;
          pending.appendChild(summary);
          var body = document.createElement('div');
          body.className = 'nsc-faq__a';
          pending.appendChild(body);
          container.appendChild(pending);
          node.remove();
          made++;
        } else if (pending && text) {
          pending.querySelector('.nsc-faq__a').appendChild(node);
        }
        node = next;
      }

      if (made >= 2) faqHeading.after(container);
    }
  }

  /* ---------- 6. Inline product card after the Nth H2 ---------- */
  var ctaAfter = parseInt(root.dataset.ctaAfter || '0', 10);
  var railCard = root.querySelector('.nsc-rail__product .nsc-pcard');

  if (ctaAfter > 0 && railCard && h2s.length > ctaAfter) {
    var anchorH2 = h2s[ctaAfter - 1];
    var stop = h2s[ctaAfter];
    var insertBefore = stop || null;

    var inline = document.createElement('aside');
    inline.className = 'nsc-inline-cta';
    inline.innerHTML =
      '<p class="nsc-inline-cta__label">Made by us</p>' +
      railCard.outerHTML;
    inline.querySelector('.nsc-pcard').classList.remove('nsc-pcard--compact');

    if (insertBefore) {
      insertBefore.parentNode.insertBefore(inline, insertBefore);
    } else {
      anchorH2.parentNode.appendChild(inline);
    }
  }

  /* ---------- 7. Reading progress ---------- */
  var bar = document.querySelector('.nsc-progress__bar');
  if (bar) {
    var ticking = false;
    var update = function () {
      var rect = prose.getBoundingClientRect();
      var total = rect.height - window.innerHeight;
      var scrolled = Math.min(Math.max(-rect.top, 0), Math.max(total, 1));
      bar.style.transform = 'scaleX(' + (total > 0 ? scrolled / total : 0) + ')';
      ticking = false;
    };
    window.addEventListener('scroll', function () {
      if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  /* ---------- 8. Sticky mobile CTA: show after the first screen ---------- */
  var sticky = document.querySelector('[data-nsc-sticky]');
  if (sticky && 'IntersectionObserver' in window) {
    var trigger = root.querySelector('.nsc-head');
    if (trigger) {
      new IntersectionObserver(function (entries) {
        sticky.hidden = entries[0].isIntersecting;
      }, { threshold: 0 }).observe(trigger);
    }
  }
})();
